import { useCallback, useEffect, useState } from "react";
import { pushThenNavigate } from "@/shared/lib/analytics";
import { currentReturnPath } from "@/features/tokenPurchase/model/returnPath";
import { startPlanCheckout, type PlanOffer } from "../api/supPlanApi";
import {
    trackPlanBeginCheckout,
    trackPlanCheckoutError,
    trackPlanRedirect,
    trackPlanSelect,
} from "./planAnalytics";

/** Что выбрали до ухода на Stripe — для строки «Пакет на 3 месяца — до …» после возврата. */
export const PENDING_PLAN_KEY = "pending_sup_plan";

export const rememberPendingPlan = (months: number): void => {
    try {
        sessionStorage.setItem(PENDING_PLAN_KEY, JSON.stringify({ months, at: Date.now() }));
    } catch {
        /* без sessionStorage — строка без срока */
    }
};

export const takePendingPlan = (): number | null => {
    try {
        const raw = sessionStorage.getItem(PENDING_PLAN_KEY);
        sessionStorage.removeItem(PENDING_PLAN_KEY);
        if (!raw) return null;
        const m = Number((JSON.parse(raw) as { months?: unknown }).months);
        return [1, 3, 6, 12].includes(m) ? m : null;
    } catch {
        return null;
    }
};

/**
 * Оплата пакета: сервер выбирает цену (подписчик — вживую), отдаёт ссылку
 * Stripe; после оплаты Stripe возвращает сюда же (или на урок — `returnTo`)
 * с ?plan_paid=1, и PlanPurchaseRoot перечитывает кошелёк.
 */
export const usePlanCheckout = (opts: { lang: string; place: string; returnTo?: string | null }) => {
    const { lang, place, returnTo } = opts;
    const [busy, setBusy] = useState<number | null>(null);
    const [error, setError] = useState(false);

    // Назад со Stripe кнопкой браузера — страница из bfcache с «идёт оплата».
    useEffect(() => {
        const onShow = (e: PageTransitionEvent) => {
            if (e.persisted) setBusy(null);
        };
        window.addEventListener("pageshow", onShow);
        return () => window.removeEventListener("pageshow", onShow);
    }, []);

    const buy = useCallback(
        async (offer: PlanOffer) => {
            if (busy !== null) return;
            trackPlanSelect(offer, place);
            setBusy(offer.months);
            setError(false);
            try {
                const res = await startPlanCheckout(offer.months, {
                    returnPath: returnTo || currentReturnPath(),
                    lang,
                });
                rememberPendingPlan(offer.months);
                const checkout = trackPlanBeginCheckout({ ...offer, cents: res.cents || offer.cents }, place);
                await pushThenNavigate(() => trackPlanRedirect(checkout), res.url);
            } catch (err) {
                trackPlanCheckoutError(offer, err, place);
                setBusy(null);
                setError(true);
            }
        },
        [busy, place, returnTo, lang],
    );

    return { buy, busy, error, clearError: () => setError(false) };
};
